import { useParams, Link } from 'react-router-dom';
import PageMeta from '@/components/base/PageMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles, categories } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-6">您查找的文章不存在或已被移除</p>
            <Link to="/" className="px-6 py-2.5 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <PageMeta
        title={`${article.title} - 海角社区地址导航`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://haijiao-navi.readdy.ai/article/${article.slug}`}
        ogType="article"
        ogImage={article.image}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        articlePublishedTime={article.date}
        articleAuthor={article.author}
        articleTags={article.tags}
        jsonLdScripts={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.image,
            datePublished: article.date,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            publisher: {
              '@type': 'Organization',
              name: '海角导航',
            },
            url: `https://haijiao-navi.readdy.ai/article/${article.slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '海角社区地址导航',
              url: 'https://haijiao-navi.readdy.ai/',
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://haijiao-navi.readdy.ai/' },
              { '@type': 'ListItem', position: 2, name: category?.name || article.category, item: `https://haijiao-navi.readdy.ai/category/${article.category}` },
              { '@type': 'ListItem', position: 3, name: article.title },
            ],
          },
        ]}
      />

      <Navbar />
      <Breadcrumb />

      <main>
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <header className="mb-8">
            {category && (
              <Link
                to={`/category/${category.slug}`}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800 hover:bg-secondary-200 transition-colors mb-4 whitespace-nowrap"
              >
                <i className={`${category.icon} text-xs`}></i>
                {category.name}
              </Link>
            )}
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <div className="flex flex-wrap gap-1.5">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 rounded text-xs bg-background-100 text-foreground-600 border border-background-200/70">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </header>

          <div className="w-full rounded-lg overflow-hidden mb-8">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 海角社区地址`}
              className="w-full h-auto object-cover"
            />
          </div>

          <div
            className="prose prose-base md:prose-lg max-w-none
              prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-semibold
              prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
              prose-strong:text-foreground-900 prose-strong:font-semibold
              prose-ul:my-4 prose-ol:my-4
              prose-li:text-foreground-700 prose-li:leading-relaxed prose-li:mb-1.5
              prose-a:text-primary-600 hover:prose-a:text-primary-700 prose-a:no-underline
              prose-em:text-foreground-600"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <hr className="my-10 border-background-200/70" />

          {relatedArticles.length > 0 && (
            <section>
              <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group p-4 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer"
                  >
                    <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                      {ra.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2">{ra.excerpt}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <div className="mt-10 flex flex-col sm:flex-row items-center gap-4 justify-between bg-background-100 rounded-lg p-5">
            <div>
              <p className="text-sm text-foreground-600">
                本文分类：
                {category && (
                  <Link to={`/category/${category.slug}`} className="text-primary-600 hover:text-primary-700 font-medium">
                    {category.name}
                  </Link>
                )}
              </p>
            </div>
            <div className="flex gap-3">
              <Link
                to={category ? `/category/${category.slug}` : '/'}
                className="px-5 py-2 text-sm bg-background-50 text-foreground-700 border border-background-200/70 rounded-lg hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                ← 返回分类
              </Link>
              <Link
                to="/"
                className="px-5 py-2 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}